import { Delete, Edit, MoreVert, Send } from '@mui/icons-material'
import { Divider, IconButton, Menu } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { DeleteDialog } from 'components/Dialog/DeleteDialog'
import { ListItemButton } from 'components/List'
import { useMenu } from 'hooks/useMenu'
import { useAbility } from 'providers/casl'
import { useResendInvitation } from '../hooks/useResendInvitation'
import { UserActionsCellProps } from './types'

export const UserActionsCell = ({
  disabled,
  editPath,
  id,
  loading,
  onDelete,
}: UserActionsCellProps): JSX.Element => {
  const ability = useAbility()
  const { t } = useTranslation('features/user')
  const { t: tC } = useTranslation('common')
  const navigate = useNavigate()
  const { anchor, close, isOpen, open } = useMenu()
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  const { resend } = useResendInvitation()

  const handleResend = (): void => {
    close()
    resend(id)
  }

  const handleDelete = (): void => {
    close()
    setIsDialogOpen(true)
  }

  const ResendInvitationItem = (): JSX.Element => (
    <ListItemButton
      data-cy='actions-resend-invitation'
      icon={<Send />}
      onClick={handleResend}
      text={t('resendInvitation.button')}
    />
  )

  const EditItem = (): JSX.Element => (
    <ListItemButton
      data-cy='actions-edit'
      icon={<Edit />}
      onClick={() => navigate(editPath)}
      text={tC('form.action.edit')}
    />
  )

  const DeleteItem = (): JSX.Element => (
    <ListItemButton
      data-cy='actions-delete'
      icon={<Delete />}
      onClick={handleDelete}
      text={tC('form.action.delete')}
    />
  )

  return (
    <>
      <IconButton
        disabled={disabled}
        onClick={open}
      >
        <MoreVert />
      </IconButton>

      <Menu
        anchorEl={anchor}
        onClose={close}
        open={isOpen}
      >
        {ability.can('create', 'WorkspaceUser') && <ResendInvitationItem />}
        {ability.can('update', 'WorkspaceUser') && <EditItem />}

        {ability.can('delete', 'WorkspaceUser') && [
          <Divider
            key='divider'
            sx={{ my: 1 }}
          />,
          <DeleteItem key='delete' />,
        ]}
      </Menu>

      <DeleteDialog
        dialog={{ isOpen: isDialogOpen, setOpen: setIsDialogOpen }}
        i18nResource={t}
        loading={loading}
        onDelete={onDelete}
      />
    </>
  )
}
